import { useEffect, useRef, useState } from 'react'
import { useInfiniteScroll } from './useInfiniteScroll'
import { DEFAULT_PAGE_SIZE } from '../../utils/constants'

interface PageResponse {
    data: unknown[]
    totalCount: number
}

interface Options {
    skip?: boolean
    size?: number
}

/**
 * Держит список, который догружается по мере прокрутки.
 *
 * `loader` получает номер страницы с нуля - так его ждёт бэкенд. При любом
 * изменении `deps` список сбрасывается и грузится с первой страницы.
 */
export const usePaginatedList = <R extends PageResponse>(
    loader: (page: number, size: number) => Promise<R>,
    deps: unknown[],
    { skip = false, size = DEFAULT_PAGE_SIZE }: Options = {},
) => {
    type Row = R['data'][number]

    const [rows, setRows] = useState<Row[]>([])
    const [totalCount, setTotalCount] = useState(0)
    const [loadedPages, setLoadedPages] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [lastResponse, setLastResponse] = useState<R | null>(null)

    const loaderRef = useRef(loader)
    loaderRef.current = loader
    const skipRef = useRef(skip)
    skipRef.current = skip
    // Ответы приходят не по порядку: применяем только последний запрос
    const requestIdRef = useRef(0)
    const isLoadingRef = useRef(false)

    const loadPage = async (nextPage: number) => {
        if (skipRef.current) return
        if (nextPage > 1 && isLoadingRef.current) return

        const requestId = ++requestIdRef.current
        isLoadingRef.current = true
        setIsLoading(true)

        try {
            const response = await loaderRef.current(nextPage - 1, size)

            if (requestId !== requestIdRef.current) return

            const batch = (response?.data ?? []) as Row[]
            setRows((prev) => (nextPage === 1 ? batch : [...prev, ...batch]))
            // Короткая страница означает конец списка: расхождение с totalCount
            // иначе гоняло бы наблюдатель по пустым страницам без остановки
            setTotalCount(
                batch.length < size
                    ? (nextPage - 1) * size + batch.length
                    : response.totalCount,
            )
            setLastResponse(response)
            setLoadedPages(nextPage)
        } catch (error) {
            // Без остановки наблюдатель повторял бы упавший запрос бесконечно
            if (requestId === requestIdRef.current) {
                setTotalCount((nextPage - 1) * size)
            }
        } finally {
            if (requestId === requestIdRef.current) {
                isLoadingRef.current = false
                setIsLoading(false)
            }
        }
    }

    const sentinelRef = useInfiniteScroll(
        () => loadPage(loadedPages + 1),
        rows.length < totalCount && !isLoading && loadedPages > 0,
    )

    const reload = () => {
        setRows([])
        setTotalCount(0)
        setLoadedPages(0)
        loadPage(1)
    }

    useEffect(() => {
        reload()
    }, deps)

    return {
        rows,
        totalCount,
        isLoading,
        loadedPages,
        lastResponse,
        sentinelRef,
        reload,
    }
}
